// src/pages/ArticlesPage.jsx
import { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { useTranslation } from 'react-i18next';
import SeoMeta from '../components/SeoMeta';
import { usePageMeta } from '../hooks/usePageMeta';

/* ---------- animations ---------- */
const float = keyframes`
  0% { transform: translateY(0) }
  50% { transform: translateY(-6px) }
  100% { transform: translateY(0) }
`;
const shimmer = keyframes`
  0% { background-position: 0% 50% }
  100% { background-position: 200% 50% }
`;

const Wrap = styled.div``;

/* ---------- HERO ---------- */
const Hero = styled.section`
  position: relative; overflow: hidden;
  padding: clamp(56px, 8vw, 96px) 0;
  .bg{
    position:absolute; inset:-25%;
    background:
      radial-gradient(40% 35% at 20% 10%, #ff660025, transparent 60%),
      radial-gradient(30% 30% at 85% 15%, #00c2ff22, transparent 60%),
      radial-gradient(60% 60% at 50% 120%, #9bff0022, transparent 60%);
    filter: blur(48px); transform: translateZ(0);
  }
  .inner{
    position:relative; width:${({theme})=>theme.container}; margin:0 auto;
    display:grid; grid-template-columns: 1.2fr .8fr; gap: 32px;
  }
  @media (max-width:980px){ .inner{ grid-template-columns:1fr } }

  h1{ font-size:${({theme})=>theme.font.sizes.h1}; margin-bottom:10px; }
  p.lead{ color:${({theme})=>theme.textMuted}; font-size:clamp(18px,2.1vw,20px); margin:0 0 18px; }

  .heroBadge{
    display:inline-grid; grid-auto-flow:column; gap:8px; align-items:center;
    padding:8px 12px; border:1px dashed ${({theme})=>theme.border};
    background:${({theme})=>theme.surface}; border-radius:${({theme})=>theme.radius.pill}px;
    font-size:14px; margin-bottom:12px;
  }

  .art{
    border:1px solid ${({theme})=>theme.border}; border-radius:18px; overflow:hidden;
    background:${({theme})=>theme.surface}; box-shadow:${({theme})=>theme.shadow.lg};
    min-height: 220px; display:grid; place-items:center; position:relative;
  }
  .art::after{
    content:''; position:absolute; inset:0;
    background: linear-gradient(120deg, #ffffff10, #00000000 40%) padding-box;
    background-size:200% 100%; animation:${shimmer} 5s linear infinite;
  }
`;

/* ---------- CONTROLS ---------- */
const Controls = styled.section`
  padding: 16px 0 0;
  .inner{ width:${({theme})=>theme.container}; margin:0 auto; display:grid; gap:10px; }
  .row{ display:flex; gap:10px; flex-wrap:wrap; align-items:center; }
  .search{
    flex:1 1 320px; display:flex; align-items:center; gap:8px;
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.surface};
    padding:8px 10px; border-radius:${({theme})=>theme.radius.pill}px;
  }
  .search input{
    flex:1; border:none; outline:none; background:transparent; color:${({theme})=>theme.text};
    font-size:14px;
  }
  .chips{ display:flex; gap:8px; flex-wrap:wrap; }
  .chip{
    padding:8px 12px; border-radius:${({theme})=>theme.radius.pill}px; cursor:pointer;
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.body};
    color:${({theme})=>theme.textMuted}; font-size:14px; user-select:none;
    transition: transform .2s ease;
  }
  .chip:hover{ transform: translateY(-2px) }
  .chip.active{
    background:${({theme})=>theme.surface}; color:${({theme})=>theme.text};
    box-shadow:${({theme})=>theme.shadow.sm};
  }
`;

/* ---------- GRID ---------- */
const Grid = styled.section`
  padding: 24px 0 72px;
  .inner{ width:${({theme})=>theme.container}; margin:0 auto; }
  .grid{
    display:grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap:16px;
    @media (max-width:980px){ grid-template-columns:1fr; }
  }
`;

/* ---------- CARD ---------- */
const Card = styled.article`
  position:relative; overflow:hidden;
  border:1px solid ${({theme})=>theme.border}; border-radius:${({theme})=>theme.radius.lg}px;
  background:${({theme})=>theme.surface}; box-shadow:${({theme})=>theme.shadow.sm};
  display:grid; grid-template-rows: 180px auto; transition: transform .35s ease, box-shadow .35s ease;

  &:hover{ transform: translateY(-4px); box-shadow:${({theme})=>theme.shadow.md}; }

  .media{
    position:relative; overflow:hidden; background:${({theme})=>theme.body};
    display:grid; place-items:center;
  }
  .media svg, .media img{ width:100%; height:100%; object-fit:cover; display:block; }
  .tag{
    position:absolute; top:10px; left:10px; padding:6px 10px; font-size:12px;
    border-radius:999px; background:${({theme})=>theme.body}; border:1px solid ${({theme})=>theme.border};
    backdrop-filter:saturate(160%) blur(6px);
  }
  .content{ padding:14px; display:grid; gap:10px; }
  h3{ margin:0; font-size:18px; }
  p.ex{ margin:0; color:${({theme})=>theme.textMuted}; }
  .meta{ display:flex; gap:10px; flex-wrap:wrap; font-size:12px; color:${({theme})=>theme.textMuted}; }
  .tags{ display:flex; gap:6px; flex-wrap:wrap; }
  .tags span{
    font-size:12px; padding:4px 8px; border-radius:999px; background:${({theme})=>theme.body};
    border:1px solid ${({theme})=>theme.border};
  }
  a.btn{
    justify-self:start; text-decoration:none; padding:8px 12px; border-radius:${({theme})=>theme.radius.pill}px;
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.body};
  }
`;

/* ---------- FEATURED ---------- */
const Featured = styled.section`
  padding: 32px 0 10px;
  .inner{ width:${({theme})=>theme.container}; margin:0 auto; }
  .box{
    display:grid; grid-template-columns: 1fr 1fr; gap:16px;
    border:1px solid ${({theme})=>theme.border}; border-radius:${({theme})=>theme.radius.xl}px;
    overflow:hidden; background:${({theme})=>theme.surface}; box-shadow:${({theme})=>theme.shadow.md};
  }
  @media (max-width:980px){ .box{ grid-template-columns:1fr } }

  .cover{
    min-height:280px; display:grid; place-items:center; position:relative; overflow:hidden;
    background:${({theme})=>theme.body};
  }
  .cover svg, .cover img{ width:100%; height:100%; object-fit:cover; }
  .cover .badge{
    position:absolute; top:10px; left:10px; padding:8px 12px; border-radius:999px;
    background:${({theme})=>theme.surface}; border:1px solid ${({theme})=>theme.border};
    font-size:12px;
  }
  .info{ padding:16px; display:grid; gap:12px; align-content:start; }
  .meta{ color:${({theme})=>theme.textMuted}; font-size:13px; }
  .btns{ display:flex; gap:10px; flex-wrap:wrap; }
`;

/* ---------- TECH STRIP ---------- */
const TechStrip = styled.section`
  padding: 8px 0 28px;
  .inner{ width:${({theme})=>theme.container}; margin:0 auto; }
  .row{
    display:grid; grid-template-columns: repeat(6, minmax(0,1fr)); gap:12px;
    @media (max-width:980px){ grid-template-columns: repeat(2, minmax(0,1fr)); }
  }
  .tech{
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.surface};
    border-radius:${({theme})=>theme.radius.lg}px; padding:14px;
    display:grid; gap:6px; align-content:start;
    transition: transform .3s ease, box-shadow .3s ease;
  }
  .tech:hover{ transform: translateY(-3px); box-shadow:${({theme})=>theme.shadow.sm}; }
  .icon{
    width:44px; height:44px; border-radius:12px; display:grid; place-items:center;
    background:${({theme})=>theme.body}; border:1px solid ${({theme})=>theme.border};
    animation:${float} 5s ease-in-out infinite;
  }
  h4{ margin:6px 0 0; font-size:15px; }
  p{ margin:0; color:${({theme})=>theme.textMuted}; font-size:13px; }
`;

/* ---------- data ---------- */
const CATS = ['Всі', 'Механічна обробка', 'Зварювання', 'Термообробка', 'Інженерні системи', 'Матеріали', 'Поради'];

const ICONS = {
  'Механічна обробка': (<path d="M4 8h8l2 3H6v2h10l2 3H4V8zm13-5l3 3-4 4-3-3 4-4z" />),
  'Зварювання': (<path d="M3 13h8v2H3v-2zm10-7h2v12h-2V6zm3 2l6 5-6 5V8z" />),
  'Термообробка': (<path d="M12 3s6 8 6 12a6 6 0 11-12 0c0-4 6-12 6-12z" />),
  'Інженерні системи': (<path d="M4 6h16v4H4V6zm0 6h12v4H4v-4zm14 0h2v6h-2v-6z" />),
  'Матеріали': (<path d="M4 8l8-4 8 4-8 4-8-4zm0 6l8 4 8-4" />),
  'Поради': (<path d="M12 2a7 7 0 017 7c0 2.5-1.5 4-3 5a3 3 0 00-2 3v1h-4v-1a3 3 0 00-2-3c-1.5-1-3-2.5-3-5a7 7 0 017-7zm-2 18h4v2h-4z" />),
  tag: (<path d="M3 12l9-9 9 9-9 9-9-9zm13-4a2 2 0 11-4 0 2 2 0 014 0z" />),
  clock: (<path d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 5h-2v6l5 3 1-1-4-2V7z" />),
  doc:   (<path d="M6 2h8l4 4v14a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2zm8 5h4l-4-4v4z" />),
};

/* улучшенная SVG-обложка з м’якою сіткою/градієнтом */
function CoverSVG({ category }) {
  const color =
    category === 'Механічна обробка' ? '#00c2ff' :
    category === 'Зварювання' ? '#ff6600' :
    category === 'Термообробка' ? '#ff3366' :
    category === 'Інженерні системи' ? '#9bff00' :
    category === 'Матеріали' ? '#8a7dff' :
    '#999';

  return (
    <svg viewBox="0 0 800 360" preserveAspectRatio="xMidYMid slice" role="img" aria-label={category}>
      <defs>
        <linearGradient id="g1" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={color} stopOpacity="0.35" />
          <stop offset="1" stopColor={color} stopOpacity="0.05" />
        </linearGradient>
        <radialGradient id="g2" cx="20%" cy="10%" r="60%">
          <stop offset="0" stopColor={color} stopOpacity="0.35" />
          <stop offset="1" stopColor={color} stopOpacity="0" />
        </radialGradient>
        <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke={color} strokeOpacity="0.08" strokeWidth="1"/>
        </pattern>
      </defs>
      <rect width="800" height="360" fill="url(#g1)" />
      <rect width="800" height="360" fill="url(#grid)" />
      <circle cx="140" cy="80" r="150" fill="url(#g2)" />
      <circle cx="680" cy="60" r="120" fill="url(#g2)" />
      <g transform="translate(120,90) scale(8)" fill="currentColor" opacity="0.6">
        {ICONS[category] || ICONS['Поради']}
      </g>
    </svg>
  );
}

/* small icon bubble for cards */
function BubbleIcon({ category }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      {ICONS[category] || ICONS['Поради']}
    </svg>
  );
}

/* ---------- генератор статей: по 11 на напрям ---------- */
const IMG_FOLDER = {
  'Механічна обробка': 'metal',
  'Зварювання': 'weld',
  'Термообробка': 'heat',
  'Інженерні системи': 'systems',
  'Матеріали': 'materials',
  'Поради': 'tips',
};

const TITLE_SEEDS = {
  'Механічна обробка': [
    'Лазерне різання: точність і швидкість',
    'Шліфування валів: контроль співвісності',
    'Оснастка для великогабаритів',
    'Зубонарізання великих діаметрів',
    'Токарно-фрезерні звʼязки',
  ],
  'Зварювання': [
    'Наплавлення зносостійкими матеріалами',
    'Роботизована зварювальна комірка',
    'Зварювання тонколистової сталі',
    'Контроль геометрії після зварювання',
    'Безпека та вентиляція при зварюванні',
  ],
  'Термообробка': [
    'Гарт та відпуск: базові режими',
    'Нормалізація для стабільності',
    'Вакуумна термообробка',
    'Контроль твердості',
    'Запобігання деформаціям',
  ],
  'Інженерні системи': [
    'CCTV: покриття та PoE',
    'СКД: карти + біометрія',
    'Автоматика воріт',
    'Електрощити та резерв',
    'Вентиляція з рекуперацією',
  ],
  'Матеріали': [
    'HPL: властивості та монтаж',
    'Антивандальні панелі',
    'Композити в інтерʼєрах',
    'Антибактеріальні поверхні',
    'UV-стійкі покриття',
  ],
  'Поради': [
    'Налагодження: швидкі чек-листи',
    'Контроль якості: протоколи',
    'Оптимізація часу переналагоджень',
    'Зниження браку на запуску партії',
    'Читання креслень без помилок',
  ],
};

function genForCategory(category, count = 11) {
  const folder = IMG_FOLDER[category] || 'misc';
  const seeds = TITLE_SEEDS[category] || ['Нотатки', 'Практика', 'Кейси'];
  const items = [];
  for (let i = 1; i <= count; i++) {
    const title = `${seeds[(i-1) % seeds.length]} — #${i}`;
    const id = `${folder}-${String(i).padStart(2,'0')}`;
    const img = `/img/articles/${folder}-${String(((i-1)%12)+1).padStart(2,'0')}.jpg`; // 12 циклічних зображень
    const date = new Date(2025, (i%12), Math.max(1, (i%28))).toISOString().slice(0,10);
    items.push({
      id,
      title,
      date,
      category,
      tags: ['технології', folder],
      excerpt: 'Короткий огляд з практичними висновками та рекомендаціями для інженерів та технологів.',
      img,
    });
  }
  return items;
}

/* збираємо всі категорії, крім "Всі" */
const ALL = [
  ...genForCategory('Механічна обробка', 11),
  ...genForCategory('Зварювання', 11),
  ...genForCategory('Термообробка', 11),
  ...genForCategory('Інженерні системи', 11),
  ...genForCategory('Матеріали', 11),
  ...genForCategory('Поради', 11),
];

/* технології у стрічці */
const TECH = [
  { k:'print3d',  title:'3D-друк металом', desc:'Порошкові сплави та лазерне спікання для унікальних виробів.', icon:<path d="M3 12h18v2H3z"/> },
  { k:'ai-cam',   title:'AI-аналітика CCTV', desc:'Виявлення подій і трекінг об’єктів на відео потоці.', icon:<path d="M4 8h9l3-2h4v10h-4l-3-2H4z"/> },
  { k:'hvof',     title:'HVOF-напилення', desc:'Надтверді покриття з низькою пористістю.', icon:<path d="M3 13h8v2H3zM13 6h2v12h-2z"/> },
  { k:'vacuum-ht',title:'Вакуумна термообробка', desc:'Гартування без окалини, стабільна геометрія.', icon:<path d="M12 3s6 8 6 12a6 6 0 11-12 0z"/> },
  { k:'rekuper',  title:'Рекуперація повітря', desc:'Енергоефективність у вентиляції та кондиціюванні.', icon:<path d="M12 3a9 9 0 100 18"/> },
  { k:'laser-cut',title:'Лазерна різка 2D/3D', desc:'Точність до 0.01 мм, чистий край після різу.', icon:<path d="M3 6h18v3H3zM3 15h18v3H3z"/> },
];

/* ---------- helpers ---------- */
const fmtDate = (d) => new Date(d).toLocaleDateString('uk-UA');
const readingTime = (txt) => Math.max(1, Math.round((txt.split(/\s+/).length + 500) / 200));

/* ---------- pagination (cap 15) ---------- */
function Pagination({ page, pages, onPage }) {
  if (pages <= 1) return null;
  const list = [];
  for (let i = 1; i <= pages; i++) list.push(i);
  return (
    <div style={{display:'flex', gap:8, justifyContent:'center', paddingTop:18}}>
      <button className="btn btn--ghost" onClick={()=>onPage(Math.max(1, page-1))} disabled={page===1}>‹</button>
      {list.map(n=>(
        <button
          key={n}
          className="btn"
          style={{
            border: '1px solid var(--border)',
            background: n===page ? 'var(--surface)' : 'var(--body)'
          }}
          onClick={()=>onPage(n)}
        >
          {n}
        </button>
      ))}
      <button className="btn btn--ghost" onClick={()=>onPage(Math.min(pages, page+1))} disabled={page===pages}>›</button>
    </div>
  );
}

/* ---------- component ---------- */
export default function ArticlesPage() {
  const { t } = useTranslation();
  const meta = usePageMeta('articles');
  const [query, setQuery] = useState('');
  const [cat, setCat] = useState('Всі');
  const [page, setPage] = useState(1);

  const PAGE_SIZE = 15; // ≤ 15 на странице

  useEffect(() => {
    AOS.init({ once: true, duration: 700, offset: 80, easing: 'ease-out-cubic' });
  }, []);
  useEffect(() => { setPage(1); }, [query, cat]); // сброс страницы при смене фильтров

  const sorted = useMemo(
    () => [...ALL].sort((a, b) => new Date(b.date) - new Date(a.date)),
    []
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sorted.filter(a => {
      const okCat = cat === 'Всі' || a.category === cat;
      const okQuery = !q || [a.title, a.excerpt, a.category, ...(a.tags||[])].join(' ').toLowerCase().includes(q);
      return okCat && okQuery;
    });
  }, [sorted, query, cat]);

  const featured = filtered[0] || sorted[0];

  // текущая страница (пропускаем featured в листинге)
  const items = filtered.slice(1);
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const pageItems = items.slice(start, start + PAGE_SIZE);

  return (
    <Wrap>
      <SeoMeta
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        image={meta.image}
        path={meta.path || '/articles'}
      />
      {/* HERO */}
      <Hero>
        <div className="bg" aria-hidden />
        <div className="inner">
          <div data-aos="fade-up">
            <span className="heroBadge">
              <BubbleIcon category="Інженерні системи" /> {t('articles.kicker', 'База знань MOMV')}
            </span>
            <h1>{t('articles.title', 'Статті та корисні матеріали')}</h1>
            <p className="lead">
              {t('articles.lead', 'Пишемо про механічну обробку, зварювання, термообробку, інженерні системи та матеріали. Практика, кейси, поради.')}
            </p>
            <div style={{display:'flex', gap:10, flexWrap:'wrap'}}>
              <Link className="btn btn--primary" to="/contacts">{t('articles.ctaTalk', 'Поставити питання')}</Link>
              <a className="btn btn--ghost" href="#list">{t('articles.ctaBrowse', 'Перейти до списку')}</a>
            </div>
          </div>
          <div data-aos="zoom-in" data-aos-delay="120" className="art" aria-hidden>
            {featured?.img
              ? <img src={featured.img} alt={featured.title} />
              : <CoverSVG category={featured?.category || 'Матеріали'} />}
          </div>
        </div>
      </Hero>

      {/* FEATURED */}
      {featured && (
        <Featured>
          <div className="inner">
            <div className="box" data-aos="fade-up">
              <div className="cover">
                <span className="badge">{featured.category}</span>
                {featured.img
                  ? <img src={featured.img} alt={featured.title} />
                  : <CoverSVG category={featured.category} />}
              </div>
              <div className="info">
                <div className="meta">{fmtDate(featured.date)} • ~{readingTime(featured.excerpt)} хв читання</div>
                <h2 style={{margin:'0'}}>{featured.title}</h2>
                <p style={{margin:'0', color:'var(--textMuted)'}}>{featured.excerpt}</p>
                <div className="btns">
                  <Link className="btn btn--primary" to={`/articles/${featured.id}`}>{t('articles.read', 'Читати')}</Link>
                  <Link className="btn btn--ghost" to="/articles">{t('articles.all', 'Усі статті')}</Link>
                </div>
              </div>
            </div>
          </div>
        </Featured>
      )}

      {/* CONTROLS */}
      <Controls>
        <div className="inner" data-aos="fade-up">
          <div className="row">
            <div className="search" role="search">
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden fill="currentColor">
                <path d="M15.5 14h-.79l-.28-.27a6.5 6.5 0 10-1.06 1.06l.27.28v.79L20 21.5 21.5 20 15.5 14zM10 15a5 5 0 110-10 5 5 0 010 10z"/>
              </svg>
              <input
                value={query}
                onChange={e=>setQuery(e.target.value)}
                placeholder={t('articles.search', 'Пошук статей...')}
                aria-label={t('articles.search', 'Пошук статей...')}
              />
            </div>
            <div className="chips" role="tablist" aria-label="Категорії">
              {CATS.map(c => (
                <button
                  key={c}
                  className={`chip ${cat===c?'active':''}`}
                  onClick={()=>setCat(c)}
                  role="tab"
                  aria-selected={cat===c}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Controls>

      {/* TECH STRIP (иконки/технологии) */}
      <TechStrip>
        <div className="inner">
          <div className="row" data-aos="fade-up">
            {TECH.map((tch, i)=>(
              <div className="tech" key={tch.k} data-aos="zoom-in" data-aos-delay={i*50}>
                <div className="icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">{tch.icon}</svg>
                </div>
                <h4>{tch.title}</h4>
                <p>{tch.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </TechStrip>

      {/* LIST */}
      <Grid id="list">
        <div className="inner">
          {pageItems.length === 0 && (
            <div style={{padding:'24px 0', color:'var(--textMuted)'}} data-aos="fade-up">
              {t('articles.noResults', 'Нічого не знайдено. Змініть фільтри або пошуковий запит.')}
            </div>
          )}
          <div className="grid">
            {pageItems.map((a, i)=>(
              <Card key={a.id} data-aos="fade-up" data-aos-delay={i%3*70}>
                <div className="media" aria-hidden>
                  <span className="tag"><BubbleIcon category={a.category} /> {a.category}</span>
                  {a.img ? <img src={a.img} alt={a.title} /> : <CoverSVG category={a.category} />}
                </div>
                <div className="content">
                  <div className="meta">
                    {fmtDate(a.date)} • ~{readingTime(a.excerpt)} хв
                  </div>
                  <h3>{a.title}</h3>
                  <p className="ex">{a.excerpt}</p>
                  <div className="tags">
                    {a.tags?.map(tg => <span key={tg}>#{tg}</span>)}
                  </div>
                  <Link className="btn" to={`/articles/${a.id}`}>{t('articles.read', 'Читати')}</Link>
                </div>
              </Card>
            ))}
          </div>

          <Pagination page={page} pages={pages} onPage={setPage} />
        </div>
      </Grid>
    </Wrap>
  );
}
